<template>
  <v-container fluid class="pa-0">
    <v-stepper v-model="stepper" class="stepper">
      <v-stepper-header class="stepper-header">
        <v-stepper-step :complete="stepper > 1" step="1">{{
          $t("pickupService.stepperHeader.pickup")
        }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="stepper > 2" step="2">{{
          $t("pickupService.stepperHeader.destination")
        }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :complete="stepper > 3" step="3">{{
          $t("pickupService.stepperHeader.request")
        }}</v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Pickup -->
        <v-stepper-content step="1">
          <v-form @submit.prevent="nextStep">
            <v-container>
              <v-menu
                v-model="timePickerVisible"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="pickupTime12hr"
                    :label="$t('pickupService.pickup.time')"
                    prepend-icon="mdi-clock"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="timeRules"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-model="pickupTime"
                  format="ampm"
                  :allowed-minutes="allowedStep"
                  full-width
                  @input="timePickerVisible = false"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="validatePickupTime"
                    >OK</v-btn
                  >
                </v-time-picker>
              </v-menu>
              <v-menu
                v-model="pickupSelectionVisible"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
                :disabled="availableLocations.length === 0"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="pickupLocation"
                    :label="$t('pickupService.pickup.location')"
                    prepend-icon="mdi-map-marker"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="pickupLocationRules"
                  >
                    <template v-slot:append>
                      <v-btn
                        v-if="resortMap && resortMap.length > 0"
                        icon
                        @click="openMap"
                      >
                        <v-icon>mdi-map</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </template>
                <v-list>
                  <v-list-item
                    v-for="location in availableLocations"
                    :key="location"
                    @click="selectLocation(location)"
                  >
                    <v-list-item-title>{{ location }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-container>
                <v-row justify="space-between">
                  <v-col cols="6"> </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn
                      color="primary"
                      :disabled="!pickupTime || !pickupLocation"
                      @click="nextStep"
                      >{{ $t("pickupService.buttons.continue") }}</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-container>
          </v-form>
        </v-stepper-content>

        <!-- Destination -->
        <v-stepper-content step="2">
          <v-form ref="destination" @submit.prevent="nextStep">
            <v-container>
              <v-menu
                v-model="destinationSelectionVisible"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="destination"
                    :label="$t('pickupService.destination.location')"
                    prepend-icon="mdi-map-marker-check"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="destinationRules"
                  >
                    <template v-slot:append>
                      <v-btn icon @click="openMap">
                        <v-icon>mdi-map</v-icon>
                      </v-btn>
                    </template></v-text-field
                  >
                </template>
                <v-list>
                  <v-list-item
                    v-for="location in destinations"
                    :key="location"
                    @click="
                      selectLocation(location, 'destinationSelectionVisible')
                    "
                  >
                    <v-list-item-title>{{ location }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-container>
                <v-row justify="space-between">
                  <v-col cols="6">
                    <v-btn color="primary" @click="previousStep">Back</v-btn>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn
                      color="primary"
                      :disabled="!destination"
                      @click="nextStep"
                      >{{ $t("pickupService.buttons.continue") }}</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-container>
          </v-form>
        </v-stepper-content>

        <!-- Request -->
        <v-stepper-content step="3">
          <v-container>
            <v-row>
              <v-col cols="12">
                <h2 class="text-h5">
                  {{ $t("pickupService.request.requestDetails") }}
                </h2>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="pickupTime"
                  :label="$t('pickupService.pickup.time')"
                  prepend-icon="mdi-clock"
                  outlined
                  dense
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="pickupLocation"
                  :label="$t('pickupService.pickup.location')"
                  prepend-icon="mdi-map-marker"
                  outlined
                  dense
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="destination"
                  :label="$t('pickupService.destination.location')"
                  prepend-icon="mdi-map-marker-check"
                  outlined
                  dense
                  readonly
                >
                  <template v-slot:append>
                    <v-btn icon @click="openMap">
                      <v-icon>mdi-map</v-icon>
                    </v-btn>
                  </template></v-text-field
                >
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="additionalNotes"
                  :label="$t('pickupService.request.additionalNotes')"
                  outlined
                  dense
                ></v-textarea>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-alert outlined type="info" icon="mdi-information-outline">
                  {{ $t("pickupService.request.receptionInfo") }}
                </v-alert>
              </v-col>
            </v-row>
            <v-row>
              <v-container>
                <v-row justify="space-between">
                  <v-col cols="6">
                    <v-btn color="primary" @click="previousStep">
                      {{ $t("pickupService.buttons.back") }}</v-btn
                    >
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn
                      color="primary"
                      :disabled="!destination"
                      @click="confirmRequest"
                      >{{ $t("pickupService.buttons.confirm") }}</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-row>
          </v-container>
        </v-stepper-content>
        <v-stepper-content step="4">
          <v-container>
            <v-row>
              <v-col cols="12">
                <h2 class="text-h5">
                  {{ $t("pickupService.requestSummary") }}
                </h2>
                <p>
                  {{ replacePlaceholders(confirmationMessage) }}
                </p>
              </v-col>
            </v-row>
          </v-container>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import { mapActions } from "vuex";

export default {
  data() {
    return {
      stepper: 1,
      pickupTime: null,
      pickupLocation: null,
      destination: null,
      additionalNotes: null,
      emailTo: "",
      emailSubject: "",
      emailMessage: "",
      confirmationMessage: "",
      availableLocations: [],
      destinations: [],
      timePickerVisible: false,
      pickupSelectionVisible: false,
      destinationSelectionVisible: false,
      minDate: new Date().toISOString().substr(0, 10),
      minTime: new Date(new Date().getTime() + 60000),
      allowedStep: (m) => m % 5 === 0,
      resortMap: null,
      mapDialogOpen: false,
    };
  },
  props: {
    list: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    ...mapActions("pickupservice", ["transmitPickupServiceRequest"]),
    openMap() {
      if (this.resortMap) {
        const pdfUrl = this.$config.managerMedia + this.resortMap;
        window.open(pdfUrl, "_blank");
      }
    },

    nextStep() {
      if (this.stepper === 1) {
        if (!this.pickupTime || !this.pickupLocation) {
          return;
        }
      }
      if (this.stepper === 2) {
        if (!this.destination || this.destination === this.pickupLocation) {
          return;
        }
      }
      this.stepper++;
    },
    previousStep() {
      this.stepper--;
    },
    confirmRequest() {
      if (this.statusTransmitPickupService === "sending") {
        return;
      }

      this.transmitPickupServiceRequest({
        emailTo: this.replacePlaceholders(this.emailTo),
        emailSubject: this.replacePlaceholders(this.emailSubject),
        emailMessage: this.replacePlaceholders(this.emailMessage),
      });

      this.stepper = 4;
    },
    selectLocation(location, menu) {
      if (menu === "destinationSelectionVisible") {
        this.destination = location;
        this.destinationSelectionVisible = false;
      } else {
        this.pickupLocation = location;
        this.pickupSelectionVisible = false;
        this.destinations = this.availableLocations.filter(
          (loc) => loc !== location
        );
      }
    },
    validatePickupDate() {
      if (this.pickupDate) {
        this.nextStep();
      }
    },
    validatePickupTime() {
      if (this.pickupTime) {
        this.nextStep();
      }
    },
    validatedestination() {
      if (this.destination) {
        this.nextStep();
      }
    },

    replacePlaceholders(template) {
      // dynamically substitutes placeholders in a template string with actual values from component data
      const placeholders = template.match(/\{([^}]+)\}/g) || [];
      let replacedMessage = template;

      placeholders.forEach((placeholder) => {
        const propName = placeholder.slice(1, -1);
        const propValue = this[propName];
        replacedMessage = replacedMessage.replace(placeholder, propValue || "");
      });

      return replacedMessage;
    },
  },
  computed: {
    room() {
      return this.$store.state.session.authResult.checkin?.[0]?.room;
    },
    name() {
      return this.$store.state.session.authResult.checkin?.[0]?.name;
    },
    pickupTime12hr() {
      if (!this.pickupTime) return "";

      const [hours, minutes] = this.pickupTime.split(":").map(Number);
      const timeFormat = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;

      return `${formattedHours}:${
        minutes < 10 ? "0" + minutes : minutes
      } ${timeFormat}`;
    },
    timeRules() {
      return [
        !!this.pickupTime12hr || this.$t("pickupService.validation.time"),
      ];
    },
    pickupLocationRules() {
      return [
        !!this.pickupLocation ||
          this.$t("pickupService.validation.pickupLocation"),
      ];
    },
    destinationRules() {
      return [
        !!this.destination || this.$t("pickupService.validation.destination"),
      ];
    },
  },
  watch: {
    pickupTime12hr(val) {
      if (!val) this.$refs.destination.resetValidation();
    },
    pickupLocation(val) {
      if (!val) this.$refs.destination.resetValidation();
    },
    destination(val) {
      if (!val) this.$refs.destination.resetValidation();
    },
  },
  created() {
    this.availableLocations = [];

    for (const item of this.list) {
      if (item.Locations) {
        const locations = item.Locations.split(";").map((location) =>
          location.trim()
        );
        this.availableLocations.push(...locations);
      }

      if (typeof item.ResortMap === "string" && item.ResortMap.trim() !== "") {
        this.resortMap = item.ResortMap;
      }

      if (
        typeof (item.EmailTo && item.EmailSubject && item.EmailMessage) ===
        "string"
      ) {
        this.emailTo = item.EmailTo;
        this.emailSubject = item.EmailSubject;
        this.emailMessage = item.EmailMessage;
      }

      if (typeof item.Confirmation) {
        this.confirmationMessage = item.ConfirmationMessage;
      }
    }
  },
};
</script>

<style scoped>
.stepper-header {
  background-color: #f5f5f5;
}

.stepper-header .v-stepper-step {
  color: #999;
}

.stepper-header .v-stepper-step.complete {
  color: #4caf50;
}

.stepper-header .v-stepper-step.active {
  color: #1976d2;
}

.stepper-header .v-stepper-step.disabled {
  color: #e0e0e0;
}

.text-h5 {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
}
</style>
